import { useState } from "react";
import { Bug, Check, Loader2 } from "lucide-react";
import { useAuthedServerFn } from "@/lib/server-fn-auth";
import { submitErrorReport } from "@/lib/bug-reports.functions";
import { readConsoleBuffer, scrubSensitive } from "@/lib/console-buffer";
import { useAuth } from "@/lib/auth-context";

declare const __BUILD_VERSION__: string;
const APP_VERSION =
  typeof __BUILD_VERSION__ !== "undefined" ? __BUILD_VERSION__ : "dev";

/**
 * Inline button rendered on the "Something went wrong" error page.
 * One click submits everything we have. Only shown when the user is
 * signed in (bug reports are login-only).
 */
export function ErrorReportButton({ error }: { error: Error }) {
  const { session } = useAuth();
  const submitFn = useAuthedServerFn(submitErrorReport);
  const [state, setState] = useState<"idle" | "sending" | "sent" | "error">(
    "idle",
  );

  if (!session) return null;

  const handleClick = async () => {
    if (state === "sent" || state === "sending") return;
    setState("sending");
    try {
      await submitFn({
        data: {
          url: typeof window !== "undefined" ? scrubSensitive(window.location.href) : undefined,
          routePattern:
            typeof window !== "undefined" ? window.location.pathname : undefined,
          userAgent: typeof navigator !== "undefined" ? navigator.userAgent : undefined,
          viewport:
            typeof window !== "undefined"
              ? `${window.innerWidth}x${window.innerHeight}`
              : undefined,
          appVersion: APP_VERSION,
          consoleBuffer: readConsoleBuffer(),
          errorMessage: error.message ? scrubSensitive(error.message).slice(0, 2000) : undefined,
          errorStack: error.stack ? scrubSensitive(error.stack).slice(0, 20000) : undefined,
        },
      });
      setState("sent");
    } catch {
      setState("error");
    }
  };

  return (
    <button
      onClick={handleClick}
      disabled={state === "sending" || state === "sent"}
      className="inline-flex items-center justify-center gap-1.5 rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent disabled:opacity-70"
    >
      {state === "idle" && (
        <>
          <Bug className="h-4 w-4" /> Report this bug
        </>
      )}
      {state === "sending" && (
        <>
          <Loader2 className="h-4 w-4 animate-spin" /> Reporting…
        </>
      )}
      {state === "sent" && (
        <>
          <Check className="h-4 w-4 text-primary" /> Reported — thanks!
        </>
      )}
      {state === "error" && <>Try again</>}
    </button>
  );
}
